import { useCallback, useRef, useState } from 'react';

type ValidatorFn = (value: string) => string | undefined | Promise<string | undefined>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FieldValues = Record<string, any>;

interface FormErrors {
  [key: string]: string | undefined;
}

interface RegisterOptions {
  validate?: ValidatorFn;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
}

export const useFieldForm = () => {
  const [fields, setFields] = useState<FieldValues>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const validators = useRef<{ [key: string]: ValidatorFn | undefined }>({}).current;

  const register = useCallback((name: string, option?: RegisterOptions) => {
    validators[name] = option?.validate;
    setFields((prevFields) => ({ ...prevFields, [name]: option?.defaultValue }));
  }, [fields, validators]);

  const onChange = useCallback((name: string, value: any) => {
    setFields((prevFields) => ({ ...prevFields, [name]: value }));
  }, [fields, validators]);

  const trigger = useCallback(async (name: string) => {
    if (validators[name]) {
      const value = fields[name];
      const error = await validators[name]?.(value);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
      return error;
    }
  }, [fields, validators]);

  const setValue = useCallback((name: string, value: string) => {
    setFields((prevFields) => ({ ...prevFields, [name]: value }));
    trigger(name);
  }, [trigger]);

  const setError = useCallback((name: string, error: string | undefined) => {
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  }, []);

  return {
    fields,
    errors,
    register,
    onChange,
    trigger,
    setValue,
    setError,
  };
};
